import axios, { Axios } from 'axios';
import { BlofinContext } from '../../blofin-provider/auth/BlofinProvider';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import usePriority from '@blofin/blofin-utils/priority/usePriority';

import { PRIORITY_LEVELS } from '@blofin/blofin-utils/priority/taskManager';

type ColorData = {
  color: string;
  country: string;
  city?: string;
};

const useColor = () => {
  const [open, setOpen] = useState(false);

  const { state } = useContext(BlofinContext);

  const { baseUrl, http = axios } = state;

  const [colorState, setColorState] = useState<ColorData>();

  const { granted, release } = usePriority({ priority: PRIORITY_LEVELS.LEVEL_400 });

  const loadingRef = useRef(false);

  const getIpState = useCallback(async <T>() => {
    return (http as Axios).get<T>(`${baseUrl}/uapi/v1/cms/color`) as Promise<T>;
  }, [baseUrl, http]);

  const setOpenState = useCallback(
    (value: boolean) => {
      setOpen(value);
      if (!value) {
        release();
      }
    },
    [release, setOpen]
  );

  const getState = useCallback(async () => {
    try {
      loadingRef.current = true;
      const res = await getIpState<{ data: ColorData }>();
      setColorState(res.data);
      if (res.data.color === 'YELLOW') {
        setOpen(true);
      } else {
        release();
      }
      if (res.data.color === 'GREEN') {
        sessionStorage.setItem('ip_restricted', JSON.stringify(false));
      }
      loadingRef.current = false;
    } catch (error) {
      loadingRef.current = false;
      console.log(error);
    }
  }, [getIpState, release, setColorState, setOpen]);

  const isUk = useMemo(() => {
    return colorState?.country?.toLocaleUpperCase() === 'GB' ? true : false;
  }, [colorState]);

  const isUae = useMemo(() => {
    return colorState?.country?.toLocaleUpperCase() === 'AE' ? true : false;
  }, [colorState]);

  const isChicago = useMemo(() => {
    return colorState?.country?.toLocaleUpperCase() === 'US' && colorState?.city?.toLocaleLowerCase() === 'chicago';
  }, [colorState]);

  useEffect(() => {
    const ipFlag = sessionStorage.getItem('ip_restricted');
    if (ipFlag && JSON.parse(ipFlag)) {
      setOpenState(false);
    } else {
      if (!loadingRef.current) {
        getState();
      }
    }
  }, [setOpenState, getState]);

  return {
    open: open && granted,
    setOpen: setOpenState,
    colorState,
    isUk,
    isUae,
    isChicago,
  };
};

export default useColor;
